/**
 * Cloud Operations — Deployment Targets.
 *
 * List + create + retire. A target is the (customer, mode, cloud account,
 * region(s)) addressable destination for a workflow run. The create form
 * filters cloud accounts by mode/auth-method consistency so the form-level
 * UX matches the service-layer guard.
 *
 * Per design doc §3.2 + §5.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  listTargets, createTarget, retireTarget,
  listCloudAccounts, getSlaMatrix,
} from '../../services/cloudOperationsApi';
import {
  T, inp, inpReadOnly, btn, btnPrimary, btnSm, btnSmDanger,
  Field, Banner, Th, Td, Slideout, ProviderPill, ModePill, StatusPill,
  fmtDate,
} from './_ui';

export default function DeploymentTargetsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slideout, setSlideout] = useState(false);

  function reload() {
    setLoading(true);
    listTargets({ size: 50 })
      .then((d) => { setItems(d?.items || []); setLoading(false); })
      .catch((e) => { setError(e?.message || String(e)); setLoading(false); });
  }
  useEffect(reload, []);

  async function onRetire(row) {
    if (!window.confirm(`Retire target "${row.name}"? Existing deployments stay; future deployments cannot bind to it.`)) return;
    try { await retireTarget(row.id); reload(); }
    catch (e) { alert('Retire failed: ' + (e?.message || e)); }
  }

  return (
    <div>
      <ActionsBar onAdd={() => setSlideout(true)} />
      {error   && <Banner color={T.danger} bg={T.dangerBg}>Could not load: {error}</Banner>}
      {loading && <Banner color={T.info}   bg={T.infoBg}>Loading…</Banner>}
      {!loading && (items.length === 0
        ? <Empty />
        : <Table rows={items} onRetire={onRetire} />
      )}
      <Slideout open={slideout} onClose={() => setSlideout(false)}>
        <CreateForm onClose={() => setSlideout(false)} onSaved={() => { setSlideout(false); reload(); }} />
      </Slideout>
    </div>
  );
}

function ActionsBar({ onAdd }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ color: T.text2, fontSize: 13 }}>
        Targets are the addressable destinations a System Deployment Workflow runs against.
        Bind a cloud account on the <strong>Cloud accounts</strong> tab before creating a target.
      </div>
      <div style={{ flex: 1 }} />
      <button onClick={onAdd} style={btnPrimary}>+ Add target</button>
    </div>
  );
}

function Empty() {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
      padding: 40, textAlign: 'center', color: T.text3, fontStyle: 'italic',
    }}>
      No deployment targets yet. Click <strong>Add target</strong> to create one — a
      verified cloud account is required.
    </div>
  );
}

function Table({ rows, onRetire }) {
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: T.bgTint }}>
            <Th>Target</Th><Th>Mode</Th><Th>Primary region</Th>
            <Th>Paired</Th><Th>SLA tier</Th><Th>Status</Th><Th>Created</Th><Th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
              <Td>
                <div style={{ fontWeight: 600, color: T.ink }}>{r.name}</div>
                <div style={{ color: T.text3, fontSize: 11 }}>customer · {r.customer_id}</div>
              </Td>
              <Td><ModePill mode={r.mode} /></Td>
              <Td><code style={{ fontSize: 12 }}>{r.primary_region}</code></Td>
              <Td>{r.paired_region ? <code style={{ fontSize: 12 }}>{r.paired_region}</code> : <span style={{ color: T.text3 }}>—</span>}</Td>
              <Td><code style={{ fontSize: 11 }}>{r.sla_tier}</code></Td>
              <Td><StatusPill status={r.status} /></Td>
              <Td><span style={{ color: T.text2, fontSize: 11 }}>{fmtDate(r.created_at)}</span></Td>
              <Td><button onClick={() => onRetire(r)} style={btnSmDanger}>Retire</button></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Create slideout ─────────────────────────────────────────────────────────
function CreateForm({ onClose, onSaved }) {
  const [mode, setMode]                     = useState('prove7_hosted');
  const [name, setName]                     = useState('');
  const [cloudAccountId, setCloudAccountId] = useState('');
  const [primaryRegion, setPrimaryRegion]   = useState('');
  const [pairedRegion, setPairedRegion]     = useState('');
  const [slaTier, setSlaTier]               = useState('multi_az');
  const [dataResidency, setDataResidency]   = useState('region_locked');

  const [cloudAccounts, setCloudAccounts] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    listCloudAccounts({ size: 100 }).then((d) => setCloudAccounts(d?.items || [])).catch(() => {});
    getSlaMatrix().then(setMatrix).catch(() => {});
  }, []);

  const eligibleAccounts = useMemo(() => {
    const HOSTED = new Set(['wif', 'iam_role_oidc', 'azure_workload_identity']);
    return cloudAccounts.filter((c) => {
      if (mode === 'prove7_hosted') return HOSTED.has(c.auth_method);
      if (mode === 'self_hosted')   return c.auth_method === 'bridge_delegated';
      return true;
    });
  }, [cloudAccounts, mode]);

  // Reset cloud-account selection when mode changes since the eligible set shifts.
  useEffect(() => { setCloudAccountId(''); }, [mode]);

  const selectedAccount = useMemo(
    () => cloudAccounts.find((c) => c.id === cloudAccountId),
    [cloudAccounts, cloudAccountId]
  );
  const provider = selectedAccount?.provider;

  const regions = useMemo(() => {
    if (!matrix || !provider) return [];
    const byProvider = matrix.by_provider?.[provider] || [];
    return byProvider;
  }, [matrix, provider]);

  const multiRegion = slaTier.startsWith('multi_region_');

  // Auto-set paired_region from matrix when SLA tier flips to multi-region.
  useEffect(() => {
    if (!multiRegion) { setPairedRegion(''); return; }
    const r = regions.find((x) => x.id === primaryRegion);
    if (r?.paired_region) setPairedRegion(r.paired_region);
  }, [slaTier, primaryRegion, multiRegion, regions]);

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const payload = {
        customerId: selectedAccount?.customer_id || null,
        orgId: selectedAccount?.org_id || null,
        name,
        mode,
        cloudAccountCredentialId: cloudAccountId,
        primaryRegion,
        pairedRegion: multiRegion ? pairedRegion : null,
        slaTier,
        dataResidency,
      };
      await createTarget(payload);
      onSaved();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, height: '100%', overflow: 'auto' }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 18, color: T.ink, fontWeight: 700 }}>Add deployment target</h2>
        <p style={{ color: T.text2, fontSize: 12, marginTop: 4 }}>
          Targets are addressable destinations for System Deployment Workflows. Mode determines
          which cloud accounts are eligible.
        </p>
      </div>

      <Field label="Name" required>
        <input style={inp} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. tgt_acme_gcp_uc1" required />
      </Field>

      <Field label="Mode" required>
        <select style={inp} value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="prove7_hosted">Prove7 Cloud (managed account)</option>
          <option value="self_hosted">Self-Hosted (customer cloud + bridge agent)</option>
        </select>
      </Field>

      <Field label="Cloud account" required hint={
        mode === 'prove7_hosted'
          ? 'Filtered to Hosted-eligible accounts (WIF / IAM Role / Azure WI).'
          : 'Filtered to Self-Hosted accounts (bridge-delegated).'
      }>
        <select style={inp} value={cloudAccountId} onChange={(e) => setCloudAccountId(e.target.value)} required>
          <option value="">— pick a cloud account —</option>
          {eligibleAccounts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {c.provider?.toUpperCase()} · {c.account_identifier} · {c.status}
            </option>
          ))}
        </select>
      </Field>
      {eligibleAccounts.length === 0 && (
        <Banner color={T.warn} bg={T.warnBg}>
          No eligible cloud accounts for mode <code>{mode}</code>. Bind one on
          the Cloud accounts tab first.
        </Banner>
      )}

      <Field label="Primary region" required>
        <select style={inp} value={primaryRegion} onChange={(e) => setPrimaryRegion(e.target.value)} required disabled={!provider}>
          <option value="">{provider ? '— pick a region —' : 'Pick a cloud account first'}</option>
          {regions.map((r) => (
            <option key={r.id} value={r.id}>{r.id} · {r.location}</option>
          ))}
        </select>
      </Field>

      <Field label="SLA tier" required>
        <select style={inp} value={slaTier} onChange={(e) => setSlaTier(e.target.value)}>
          <option value="single_az">Single-AZ — dev / staging</option>
          <option value="multi_az">Multi-AZ — recommended v1 production</option>
          <option value="multi_region_active_standby">Multi-Region · Active-Standby</option>
          <option value="multi_region_active_active">Multi-Region · Active-Active</option>
        </select>
      </Field>

      {multiRegion && (
        <Field label="Paired region" required hint="Set automatically from the SLA matrix; override only if you know why.">
          <input style={inp} value={pairedRegion} onChange={(e) => setPairedRegion(e.target.value)} required />
        </Field>
      )}

      <Field label="Data residency" required>
        <select style={inp} value={dataResidency} onChange={(e) => setDataResidency(e.target.value)}>
          <option value="region_locked">Region-locked — no cross-region copy</option>
          <option value="backup_export_same_region">Backup export · same region</option>
          <option value="cross_region_to_paired">Cross-region copy to paired region</option>
        </select>
      </Field>

      {err && <Banner color={T.danger} bg={T.dangerBg}>{err}</Banner>}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 12 }}>
        <button type="button" onClick={onClose} style={btn}>Cancel</button>
        <div style={{ flex: 1 }} />
        <button type="submit" disabled={submitting} style={btnPrimary}>
          {submitting ? 'Creating…' : 'Create target'}
        </button>
      </div>
    </form>
  );
}
